import { format, parseISO, isValid } from 'date-fns';

/**
 * Safely formats a date string or Date object to a localized date string
 * @param dateValue - The date value to format (string, Date, or number)
 * @param fallback - The fallback text to display if date is invalid (default: 'N/A')
 * @returns Formatted date string or fallback text
 */
export const formatSafeDate = (dateValue: unknown, fallback: string = 'N/A'): string => {
  if (!dateValue) return fallback;
  
  try {
    let date: Date;
    
    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      // Handle empty strings
      if (dateValue.trim() === '') return fallback;
      
      // Try different date parsing approaches
      if (dateValue.includes('T') || dateValue.includes('Z')) {
        // ISO format
        date = parseISO(dateValue);
      } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // YYYY-MM-DD format
        date = parseISO(dateValue);
      } else {
        // Try general Date parsing
        date = new Date(dateValue);
      }
    } else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else {
      return fallback;
    }
    
    // Check if the date is valid
    if (!isValid(date) || isNaN(date.getTime())) {
      return fallback;
    }
    
    // Format the date
    return format(date, 'MMM d, yyyy');
    
  } catch (error) {
    console.warn('Date formatting error:', error, 'for value:', dateValue);
    return fallback;
  }
};

/**
 * Safely formats a date for display in tables with a shorter format
 * @param dateValue - The date value to format
 * @param fallback - The fallback text to display if date is invalid (default: 'N/A')
 * @returns Formatted date string or fallback text
 */
export const formatTableDate = (dateValue: unknown, fallback: string = 'N/A'): string => {
  if (!dateValue) return fallback;
  
  try {
    let date: Date;
    
    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      if (dateValue.trim() === '') return fallback;
      
      if (dateValue.includes('T') || dateValue.includes('Z')) {
        date = parseISO(dateValue);
      } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
        date = parseISO(dateValue);
      } else {
        date = new Date(dateValue);
      }
    } else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else {
      return fallback;
    }
    
    if (!isValid(date) || isNaN(date.getTime())) {
      return fallback;
    }
    
    return format(date, 'MM/dd/yyyy');
    
  } catch (error) {
    console.warn('Table date formatting error:', error, 'for value:', dateValue);
    return fallback;
  }
};

/**
 * Checks if a date value is valid
 * @param dateValue - The date value to check
 * @returns true if the date is valid, false otherwise
 */
export const isValidDate = (dateValue: unknown): boolean => {
  if (!dateValue) return false;
  
  try {
    let date: Date;
    
    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      if (dateValue.trim() === '') return false;
      date = new Date(dateValue);
    } else if (typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else {
      return false;
    }
    
    return isValid(date) && !isNaN(date.getTime());
  } catch {
    return false;
  }
};
